import React, { createContext, useContext, useState } from 'react'
import { createStore, useStore } from 'zustand'
import type { StoreApi } from 'zustand'
import { getGPUTier, TierResult } from 'detect-gpu'

import type { AddressData, Configuration, BaseAgentData, AgentData, EnvironmentPreset } from '../../models/types'
import { ContainerParent } from '../../models/enums'
import { characters } from '../../utils/defaultCharacters'
import fetchAgentData from '../../utils/fetchAgentData'

interface RetorikState {
  configuration: Configuration
  addressData: AddressData
  sendBoxRef: HTMLTextAreaElement | null
  sendBoxPlaceHolder: string | undefined
  canFocusSendBox: boolean
  fromTextToVocal: boolean
  baseAddress: string
  subtitlesInConfiguration: boolean
  displaySubtitles: boolean
  agentData: AgentData | undefined
  gpuData: TierResult | undefined
  loaderClosed: boolean
  appAvailable: boolean
  currentLayout: ContainerParent
  userAlreadySet: boolean
  isWaitingForResponse: boolean
  instantIsWaitingForResponse: boolean
  skipLoader: boolean
  displayAgentInNews: boolean
  submitDelayBeforeSendingMessage: number | undefined
  disableHistory: boolean
  environmentPreset: EnvironmentPreset
  customScripts: Array<string>
  customCSS: Array<string>
  enabledFeatures: Array<string>
  actions: {
    setConfiguration: (value: Configuration) => void
    setAddressData: (value: AddressData) => void
    setSendBoxRef: (value: HTMLTextAreaElement | null) => void
    focusSendBox: () => void
    setCanFocusSendBox: (value: boolean) => void
    setSendBoxPlaceHolder: (value: string | undefined) => void
    setLayout: (value: ContainerParent) => void
    setUserAlreadySet: (value: boolean) => void
    setEnvironmentPreset: (value: EnvironmentPreset) => void
    setFromTextToVocal: (value: boolean) => void
    setBaseAddress: (value: string) => void
    setLoaderClosed: (value: boolean) => void
    setAppAvailable: (value: boolean) => void
    setSubtitlesInConfiguration: (value: boolean) => void
    setDisplaySubtitles: (value: boolean) => void
    toggleSubtitles: () => void
    setAgentData: (value: BaseAgentData) => Promise<void>
    setTimerForWaiting: () => void
    setIsWaitingForResponse: (value: boolean) => void
    setSkipLoader: (value: boolean) => void
    setDisplayAgentInNews: (value: boolean) => void
    setSubmitDelayBeforeSendingMessage: (value: number | undefined) => void
    setDisableHistory: (value: boolean) => void
    setCustomScripts: (value: Array<string>) => void
    setCustomCSS: (value: Array<string>) => void
    setEnabledFeatures: (value: Array<string>) => void
    resetRetorikStore: () => void
  }
}

const defaultConfiguration: Configuration = {
  subtitles: false
}

const initialState = {
  configuration: defaultConfiguration,
  addressData: {},
  canFocusSendBox: false,
  sendBoxRef: null,
  sendBoxPlaceHolder: undefined,
  fromTextToVocal: false,
  baseAddress: '',
  subtitlesInConfiguration: false,
  displaySubtitles: false,
  agentData: undefined,
  gpuData: undefined,
  loaderClosed: false,
  appAvailable: false,
  currentLayout: ContainerParent.agent,
  userAlreadySet: false,
  isWaitingForResponse: false,
  instantIsWaitingForResponse: false,
  skipLoader: false,
  displayAgentInNews: true,
  submitDelayBeforeSendingMessage: undefined,
  disableHistory: false,
  environmentPreset: "apartment" as EnvironmentPreset,
  customScripts: [],
  customCSS: [],
  enabledFeatures: []
}

let timer: NodeJS.Timeout | null = null

timer = setTimeout(() => {
  return true
}, 1000)

const createRetorikStore = () => {
  return createStore<RetorikState>((set, get) => {
    return {
      ...initialState,
      actions: {
        setConfiguration: (value: Configuration): void => {
          set({ configuration: value })
        },
        setAddressData: (value: AddressData): void => {
          set({ addressData: value })
        },
        setSendBoxRef: (value: HTMLTextAreaElement | null): void => {
          set({ sendBoxRef: value })
        },
        focusSendBox: (): void => {
          const sendBox = get().sendBoxRef
          sendBox && document.activeElement !== sendBox && sendBox.focus()
        },
        setCanFocusSendBox: (value: boolean): void => {
          set({ canFocusSendBox: value })
        },
        setSendBoxPlaceHolder: (value: string | undefined): void => {
          set({ sendBoxPlaceHolder: value })
        },
        setLayout: (value: ContainerParent): void => {
          set({ currentLayout: value })
        },
        setUserAlreadySet: (value: boolean): void => {
          set({ userAlreadySet: value })
        },
        setEnvironmentPreset: (value: EnvironmentPreset): void => {
          set({ environmentPreset: value })
        },
        setFromTextToVocal: (value: boolean): void => {
          set({ fromTextToVocal: value })
        },
        setBaseAddress: (value: string): void => {
          set({ baseAddress: value })
        },
        setLoaderClosed: (value: boolean): void => {
          set({ loaderClosed: value })
        },
        setAppAvailable: (value: boolean): void => {
          set({ appAvailable: value })
        },
        setSubtitlesInConfiguration: (value: boolean): void => {
          set({
            subtitlesInConfiguration: value,
            displaySubtitles: value
          })
        },
        setDisplaySubtitles: (value: boolean): void => {
          set({ displaySubtitles: value })
        },
        toggleSubtitles: (): void => {
          set((state) => ({ displaySubtitles: !state.displaySubtitles }))
        },
        setAgentData: async (value: BaseAgentData): Promise<void> => {
          // Test GPU to use bodyengine or fall back to spiritengine. Tier at least 1 for mobile, 2 for desktop
          const gpuTier = await getGPUTier()
          let gpuTestSupportsBodyengine = false

          if (gpuTier.tier > 0) {
            if (gpuTier.type === 'FALLBACK') {
              gpuTestSupportsBodyengine = true
            } else {
              gpuTestSupportsBodyengine = gpuTier.tier > 1
            }
          }

          const url = typeof value === 'string' ? value : value.url
          let data = await fetchAgentData(url)

          if (data?.useBodyEngineThree && !gpuTestSupportsBodyengine) {
            console.log('Retorik Framework > graphic capacity too low to use bodyengine, the application will switch to spiritengine')
            data = await fetchAgentData(data?.gender === 'female' ? characters.zahra : characters.rowan)
          }

          if (data && typeof value !== 'string') {
            value.name && (data.name = value.name)
            value.animationsUrl && (data.animationsUrl = value.animationsUrl)
          }

          set({ gpuData: gpuTier, agentData: data })
        },
        setTimerForWaiting: (): void => {
          timer = setTimeout(() => {
            set({ isWaitingForResponse: true })
            timer = null
          }, 1000)
        },
        setIsWaitingForResponse: (value: boolean): void => {
          if (value) {
            set({
              instantIsWaitingForResponse: true
            })
            !timer && get().actions.setTimerForWaiting()
          } else {
            timer && clearTimeout(timer)
            timer = null
            set({
              instantIsWaitingForResponse: false,
              isWaitingForResponse: false
            })
          }
        },
        setSkipLoader: (value: boolean): void => {
          set({ skipLoader: value })
        },
        setDisplayAgentInNews: (value: boolean): void => {
          set({ displayAgentInNews: value })
        },
        setSubmitDelayBeforeSendingMessage: (value: number | undefined): void => {
          set({ submitDelayBeforeSendingMessage: value })
        },
        setDisableHistory: (value: boolean): void => {
          set({ disableHistory: value })
        },
        setCustomScripts: (value: Array<string>): void => {
          set({ customScripts: value })
        },
        setCustomCSS: (value: Array<string>): void => {
          set({ customCSS: value })
        },
        setEnabledFeatures: (value: Array<string>): void => {
          set({ enabledFeatures: value })
        },
        resetRetorikStore: (): void => {
          set({ ...initialState })
        }
      }
    }
  })
}

const RetorikContext = createContext<StoreApi<RetorikState> | null>(null)

const useRetorikStore = <T,>(selector: (state: RetorikState) => T): T => {
  const store = useContext(RetorikContext)
  if (!store) throw new Error('Retorik Framework > useRetorikStore must be used within RetorikProvider')
  return useStore(store, selector)
}

const RetorikProvider = ({ children }) => {
  const [store] = useState(() => createRetorikStore())

  return <RetorikContext.Provider value={store}>{children}</RetorikContext.Provider>
}

export default RetorikProvider
export { useRetorikStore }
